import * as THREE from 'three';

interface ActiveRipple {
  mesh: THREE.Mesh;
  age: number;
  life: number;
  scaleStart: number;
  scaleEnd: number;
}

interface ActiveBurst {
  group: THREE.Group;
  geometry: THREE.BufferGeometry;
  particles: Array<{ mesh: THREE.Mesh; vel: THREE.Vector3 }>;
  age: number;
  life: number;
}

export class Effects {
  private readonly scene: THREE.Scene;
  private ripples: ActiveRipple[] = [];
  private bursts: ActiveBurst[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  spawnRipple(position: THREE.Vector3, color = 0xc8e5ff): void {
    const geo = new THREE.RingGeometry(0.4, 0.55, 24);
    geo.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(position);
    mesh.position.y = -2.45;
    this.scene.add(mesh);
    this.ripples.push({
      mesh,
      age: 0,
      life: 1.2,
      scaleStart: 1,
      scaleEnd: 4.5
    });
    console.info('[fx] ripple');
  }

  spawnBurst(position: THREE.Vector3, color: number, personaId: string): void {
    const group = new THREE.Group();
    group.position.copy(position);
    const particles: ActiveBurst['particles'] = [];
    const geo = new THREE.IcosahedronGeometry(0.18, 0);
    for (let i = 0; i < 6; i++) {
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1, depthWrite: false });
      const mesh = new THREE.Mesh(geo, mat);
      const angle = (i / 6) * Math.PI * 2 + Math.random() * 0.4;
      const upBias = 1.2 + Math.random() * 0.6;
      const vel = new THREE.Vector3(Math.cos(angle) * 1.4, upBias, Math.sin(angle) * 1.4);
      group.add(mesh);
      particles.push({ mesh, vel });
    }
    this.scene.add(group);
    this.bursts.push({ group, geometry: geo, particles, age: 0, life: 1.0 });
    console.info(`[fx] burst persona=${personaId}`);
  }

  update(dt: number): void {
    for (let i = this.ripples.length - 1; i >= 0; i--) {
      const r = this.ripples[i];
      r.age += dt;
      const k = Math.min(1, r.age / r.life);
      const s = r.scaleStart + (r.scaleEnd - r.scaleStart) * k;
      r.mesh.scale.set(s, s, s);
      const mat = r.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.85 * (1 - k);
      if (r.age >= r.life) {
        this.scene.remove(r.mesh);
        r.mesh.geometry.dispose();
        mat.dispose();
        this.ripples.splice(i, 1);
      }
    }

    for (let i = this.bursts.length - 1; i >= 0; i--) {
      const b = this.bursts[i];
      b.age += dt;
      const k = Math.min(1, b.age / b.life);
      for (const p of b.particles) {
        p.mesh.position.addScaledVector(p.vel, dt);
        p.vel.y -= 1.2 * dt;
        const mat = p.mesh.material as THREE.MeshBasicMaterial;
        mat.opacity = 1 - k;
      }
      if (b.age >= b.life) {
        for (const p of b.particles) {
          (p.mesh.material as THREE.MeshBasicMaterial).dispose();
        }
        b.geometry.dispose();
        this.scene.remove(b.group);
        this.bursts.splice(i, 1);
      }
    }
  }
}
